/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import React, { FC, KeyboardEvent, useRef, useEffect, useState } from 'react';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { ChildrenAgeCollectionType, RoomsAndGuestsProps } from './NGRoomsAndGuests.types';
import { StyledRoomsAndGuests } from './NGRoomsAndGuests.styles';
import {
  StepperTextBlock,
  Types,
  DropDownModal,
  CustomSelectBlock,
  InputTextField,
  shouldIgnoreClickOutside,
  shouldIgnoreFocusOutside,
} from '@marriott/mi-ui-library';

import { useSearchFormStore } from '../../store/searchFormStore';
import { useStaticDataContext } from '../../context/StaticDataContext';
import {
  ROOMS_AND_GUESTS,
  FOCUS_INTERACTIONS,
  UNIT_OF_ROOM,
  ROOMS_AND_GUESTS_CLICK_TRACK,
  ROOMS_CLICK_TRACK,
  ADULTS_CLICK_TRACK,
  CHILD_CLICK_TRACK,
  CHILD_AGE_CLICK_TRACK,
} from '../../store/store.constants';
import clsx from 'clsx';
import {
  LOCALES_FOR_ROOMS_AND_GUEST_DISPLAY_VALUE_CHANGES,
  searchFormClickTrackingLoc,
} from '../../utils/ngSearchConstants';
import { capitalize } from '../../utils/datepickerHelper';
import { getRoomRange, measureTextWidth } from '../../utils/searchUtils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

// Use named rather than default exports.
export const NGRoomsAndGuests: FC<RoomsAndGuestsProps> = ({
  onCancel,
  onChange: changeMobileState,
  onInteraction,
  isDesktopView,
  isAvailabilityScenario,
  isNextGenHp,
  modalId,
  isInModal,
  onMobileCompactViewClick,
  forceOpen = false,
  showDesktopFooter = false,
  applyTrackLabel,
}) => {
  const { tags, size } = Types;
  const isSearchFormActiveState = useSearchFormStore((state: any) => state?.isSearchFormActiveState);
  const setIsSearchFormActiveState = useSearchFormStore((state: any) => state?.setIsSearchFormActiveState);
  const roomsAndGuests = useSearchFormStore((state: any) => state.roomsAndGuests);
  const setSearchFormState = useSearchFormStore((state: any) => state.setSearchFormState);
  const activePopup = useSearchFormStore((state: any) => state?.activePopup);
  const setActivePopup = useSearchFormStore((state: any) => state?.setActivePopup);

  // Track if component has mounted on client to fix SSR hydration mismatch
  // SSR returns fallback width (text.length * 10), client measures actual font width
  const [isClientMounted, setIsClientMounted] = useState(false);
  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  // const roomCountMapForJPLocale: Record<string, string> = {
  //   '4ã€œ9': '4',
  //   '10ã€œ25': '10',
  //   '26ä»¥ä¸Š': '26',
  // };

  const {
    roomsAndGuestEybrowText,
    roomsAndGuestEyebrowText,
    rooms,
    room,
    guests,
    guest,
    roomsAndGuestsResetCta,
    roomsAndGuestsDoneCta,
    maxGuestPerRoom,
    adults,
    children,
    roomsAndGuestAdultHelperText,
    roomsAndGuestChildHelperText,
    maxChildAgeLabel,
    add,
    remove,
    ageLabel,
    smallerThanOneLabel,
    unitOfRoom = '',
    childAgeRequired = 'Child {x}: Age (Required)',
    roomListItems,
  } = useStaticDataContext();
  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const { ACCEPT_LANGUAGE: currentLocale } = clientEnvVars;

  const searchFormTopPosition = useSearchFormStore((state: any) => state?.searchFormTopPosition);
  // Initialize directly with forceOpen so the modal renders open on the first paint,
  // avoiding the extra render cycle that causes the visible delay on first open.
  const [popupOpenState, setPopupOpenState] = React.useState(forceOpen);
  // Track if popup has been opened at least once (to avoid calling applyBtnHandler on initial mount)
  const hasPopupBeenOpenedRef = React.useRef(forceOpen);
  // Track if Apply button was clicked (to prevent onCancel from being called after Apply)
  const applyClickedRef = React.useRef(false);

  // Reset applyClickedRef on every fresh open triggered by a forceOpen prop change.
  useEffect(() => {
    if (forceOpen) {
      applyClickedRef.current = false;
      setPopupOpenState(true);
    }
  }, [forceOpen]);

  const arrayInitializer = (length: number, key: string, incrementValue = 0): Array<Record<string, string>> =>
    Array.from({ length }, (_, i) => {
      return { [key]: `${i + incrementValue}` };
    });

  const roomsList = roomListItems as Array<{ roomQuantity: string }>;
  const roomsMinValue: { roomQuantity: string } = { ...roomsList?.[0] };
  const roomsMaxValue: { roomQuantity: string } = { ...roomsList?.[roomsList.length - 1] };

  const adultsList = arrayInitializer(+(maxGuestPerRoom as string), 'adultsQuantity', 1);
  const adultsMinValue = { ...adultsList[0] };

  const childrenList = arrayInitializer(+(maxGuestPerRoom as string), 'childrenQuantity');
  const childrenMinValue = { ...childrenList[0] };

  const childAgeList = arrayInitializer(+(maxChildAgeLabel as string) + 1, 'val', 0);
  childAgeList[0] = { val: smallerThanOneLabel };

  const childrenAgeMinValue = { ...childAgeList[0] };
  const childrenAgeMaxValue = { ...childAgeList[childAgeList.length - 1] };
  const totalGuests = +(maxGuestPerRoom as string);

  const roomsCountSaved = getRoomRange(roomsAndGuests?.numRooms, roomsList);

  const adultsCountSaved = roomsAndGuests?.numAdultsPerRoom;
  const childrenCountSaved = roomsAndGuests?.numChildrenPerRoom ?? 0;
  const childrenAgesSaved = roomsAndGuests?.childrenAges ?? [];

  // sometimes childrenAgesSaved is coming as "" causing component to break
  const childrenAgesCollectionSaved = !childrenCountSaved
    ? []
    : typeof childrenAgesSaved === 'object'
    ? childrenAgesSaved?.map((val: string, index: any) => {
        //changed to less than label since 0 is not an option anymore
        const value =
          !isDesktopView && val !== smallerThanOneLabel
            ? val
            : val === smallerThanOneLabel
            ? smallerThanOneLabel
            : childAgeList[parseInt(val, 10)]?.['val']; //if val is less than label then donot parse it
        return { id: index, value, isValueSet: value !== smallerThanOneLabel };
      })
    : childrenAgesSaved?.split(',')?.map((val: string, index: any) => {
        let value =
          !isDesktopView && val !== smallerThanOneLabel
            ? val
            : val === smallerThanOneLabel
            ? smallerThanOneLabel
            : childAgeList[parseInt(val, 10)]?.['val'];
        if (!value || value === '0') {
          value = smallerThanOneLabel;
        }
        return { id: index, value, isValueSet: value !== smallerThanOneLabel };
      });

  // Initialize states as default values
  const [roomsCount, setRoomsCount] = React.useState<Record<string, string>>({
    roomQuantity: roomsAndGuests?.numRooms,
  });
  const [adultsCount, setAdultsCount] = React.useState<Record<string, string>>({
    adultsQuantity: roomsAndGuests?.numAdultsPerRoom,
  });
  const [childrenCount, setChildrenCount] = React.useState<Record<string, string>>({
    childrenQuantity: roomsAndGuests?.numChildrenPerRoom ?? '0',
  });
  const [childrenAgeCollection, setChildrenAgeCollection] = React.useState<Array<ChildrenAgeCollectionType>>(
    childrenAgesCollectionSaved ?? []
  );

  const bodyRef = useRef<HTMLBodyElement | null>(null);
  const customSelectRef = useRef<HTMLDivElement | null>(null);
  const [buttonOffset, setButtonOffset] = React.useState<number>(0);

  useEffect(() => {
    bodyRef.current = document.body as HTMLBodyElement;
  }, []);

  //once the store value changes , set states for each of them
  useEffect(() => {
    //before setting each state , check if the previous value is not equal to new value

    if (roomsCount['roomQuantity']?.toString() !== roomsCountSaved?.toString()) {
      setRoomsCount(roomsCountSaved ? { roomQuantity: roomsCountSaved?.toString() } : roomsMinValue);
    }
    if (adultsCount['adultsQuantity']?.toString() !== adultsCountSaved?.toString()) {
      setAdultsCount(adultsCountSaved ? { adultsQuantity: adultsCountSaved } : adultsMinValue);
    }
    if (childrenCount['childrenQuantity']?.toString() !== childrenCountSaved?.toString()) {
      setChildrenCount(childrenCountSaved ? { childrenQuantity: childrenCountSaved?.toString() } : childrenMinValue);
    }
    if (childrenAgeCollection?.length !== childrenAgesSaved?.length) {
      childrenAgeCollection?.forEach((item: any, index: number) => {
        if (item[index]?.toString() !== childrenAgesSaved[index]?.toString()) {
          return false;
        }
        return true;
      });
      setChildrenAgeCollection(childrenAgesCollectionSaved ? childrenAgesCollectionSaved : []);
    }
  }, [roomsAndGuests]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      // Only run click-outside logic when popup is actually open
      if (!popupOpenState) return;

      const target = event.target as Element;

      // Ignore clicks on external modals (privacy popup, Adobe experiments, etc.)
      if (shouldIgnoreClickOutside(target)) {
        return;
      }

      // Check if the click target is not within the popup or the CustomSelectBlock
      if (
        !target.closest('.party-mix-dropdown-container') &&
        !target.closest('.party-mix-input-container') &&
        !target.closest('.party-mix-arrow-icon') &&
        !(customSelectRef.current && customSelectRef.current.contains(target))
      ) {
        setPopupOpenState(false);
        if (activePopup === 'roomsAndGuests') {
          setActivePopup(null);
        }
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupOpenState, activePopup]);

  // Close popup when another popup becomes active (popup coordination)
  useEffect(() => {
    if (activePopup && activePopup !== 'roomsAndGuests' && popupOpenState) {
      setPopupOpenState(false);
    }
  }, [activePopup]);

  useEffect(() => {
    if (popupOpenState) {
      setFocusOnFirstBtn();
      // Mark that popup has been opened at least once
      hasPopupBeenOpenedRef.current = true;
    }
    // Note: We no longer call applyBtnHandler when popup closes automatically.
    // The Apply/Done button click handler (applyBtnClickHandler in DropDownModal)
    // is responsible for calling applyBtnHandler when user explicitly confirms.
    // This prevents unwanted NLS search calls when user just opens and closes the popup.
  }, [popupOpenState, roomsCount]);

  //if user changes values using plus minus button , set store values
  useEffect(() => {
    setSearchFormState([ROOMS_AND_GUESTS], {
      [ROOMS_AND_GUESTS]: {
        numRooms: roomsCount['roomQuantity'],
        numAdultsPerRoom: parseInt(adultsCount['adultsQuantity'], 10),
        numChildrenPerRoom: parseInt(childrenCount['childrenQuantity'], 10),
        childrenAges: childrenAgeCollection?.map(obj => obj?.value),
      },
    });
    // }

    // when rooms and guests are changed update the values in formValues
  }, [roomsCount, adultsCount, childrenCount, childrenAgeCollection]);

  const setFocusOnFirstBtn = (): void => {
    const popupElemBtn = inputRef.current?.querySelector('.rg-popup button') as HTMLElement;
    popupElemBtn?.focus();
  };

  //To update the children collection based on children row
  const updateChildrenCollection = (count: number): void => {
    if (childrenAgeCollection.length !== count) {
      const childrenAgeCollectionClone: Array<ChildrenAgeCollectionType> = childrenAgeCollection?.map(element => {
        return { ...element };
      });

      //To handle decrement button click
      if (childrenAgeCollectionClone.length > count) {
        childrenAgeCollectionClone.pop();
      }
      //To handle increment button click
      if (childrenAgeCollectionClone.length < count) {
        const childToAdd = {
          id: 0,
          value: smallerThanOneLabel, // set default value to -1 before user interaction
          isValueSet: false, // set this to true on first user interaction
        };
        if (childrenAgeCollectionClone.length > 0) {
          childToAdd.id = childrenAgeCollectionClone[childrenAgeCollectionClone.length - 1].id + 1;
        }
        childrenAgeCollectionClone.push(childToAdd);
      }
      setChildrenAgeCollection(childrenAgeCollectionClone);
    }
  };

  //To reset the values in all the rows
  const resetHandler = (e?: React.MouseEvent): void => {
    e?.preventDefault();
    setFocusOnFirstBtn();
    setRoomsCount(roomsMinValue);
    setAdultsCount(adultsMinValue);
    setChildrenCount(childrenMinValue);
    setChildrenAgeCollection([]);
  };

  //To handle rooms state count change
  const roomsChangeHandler = (type: string): void => {
    if (!roomsList?.length) return;
    const i = roomsList.findIndex(ele => ele?.roomQuantity === roomsCount['roomQuantity']?.toString());
    if (i === -1) {
      setRoomsCount({ ...roomsList[0] });
    } else {
      if (type === 'inc') {
        setRoomsCount({ ...roomsList[i + 1] });
      } else if (i > 0) {
        setRoomsCount({ ...roomsList[i - 1] });
      } else {
        setRoomsCount({ ...roomsList[0] });
      }
    }
  };

  //To handle adults state count change
  const adultsChangeHandler = (type: string): void => {
    const i = adultsList.findIndex(ele => ele['adultsQuantity'] === adultsCount['adultsQuantity']?.toString());
    if (type === 'inc') {
      setAdultsCount({ ...adultsList[i + 1] });
    } else {
      setAdultsCount({ ...adultsList[i - 1] });
    }
  };

  //To handle children state count change
  const childrenChangeHandler = (type: string): void => {
    const i = childrenList.findIndex(ele => ele['childrenQuantity'] === childrenCount['childrenQuantity']?.toString());
    if (type === 'inc') {
      setChildrenCount({ ...childrenList[i + 1] });
      updateChildrenCollection(parseInt(childrenList[i + 1]['childrenQuantity'], 10));
    } else {
      setChildrenCount({ ...childrenList[i - 1] });
      updateChildrenCollection(parseInt(childrenList[i - 1]['childrenQuantity'], 10));
    }
  };

  //To handle each child state count change
  const childAgeChangeHandler = (index: number, type?: string, age?: number): void => {
    const childrenAgeCollectionClone: Array<ChildrenAgeCollectionType> = childrenAgeCollection?.map(element => {
      return { ...element };
    });
    // if value is not already set then set the value to minimum age value
    if (!childrenAgeCollectionClone[index]?.isValueSet) {
      childrenAgeCollectionClone[index].isValueSet = true;
      childrenAgeCollectionClone[index].value = childrenAgeMinValue['val'];
    }
    // } else
    if (childrenAgeCollectionClone[index]?.isValueSet) {
      const i = childAgeList.findIndex(ele => ele['val'] === childrenAgeCollectionClone[index].value);
      if (type) {
        if (type === 'inc') {
          childrenAgeCollectionClone[index].value = childAgeList[i + 1]['val'];
        } else {
          childrenAgeCollectionClone[index].value = childAgeList[i - 1]['val'];
        }
      }
    }
    if (typeof age !== 'undefined') {
      // store child's age in the clone array
      childrenAgeCollectionClone[index].value = childAgeList[age]['val'];
    }
    // update the chidlren age collection
    setChildrenAgeCollection(childrenAgeCollectionClone);
  };

  // To handle adults and following rows hide
  const checkHideRows = (): boolean => {
    if (roomsList?.findIndex(ele => ele.roomQuantity === roomsCount['roomQuantity']) >= 4) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!isDesktopView && isNextGenHp) {
      //Can't access body element without this selector.
      document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    }
    // change the height of searchform on mobile when this dropdown closes
    return () => {
      //Can't access body element without this selector.
      document.getElementsByTagName('body')[0].style.overflow = 'auto';
    };
  }, [isDesktopView, isNextGenHp]);

  // to toggle popup on desktop on button click
  const showPopup = (): void => {
    onInteraction?.();

    // Calculate button offset only when needed and only for NextGen HP Desktop
    if (isDesktopView && isNextGenHp && customSelectRef.current && buttonOffset === 0) {
      const button = customSelectRef.current.querySelector('button');
      if (button) {
        setButtonOffset(button.offsetLeft);
      }
    }

    const newState = !popupOpenState;
    setPopupOpenState(newState);
    if (newState) {
      setActivePopup('roomsAndGuests');
    } else if (activePopup === 'roomsAndGuests') {
      setActivePopup(null);
    }
    !isDesktopView && isNextGenHp && bodyRef.current && (bodyRef.current.style.overflow = 'hidden'); // Disable scrolling on the background
    setSearchFormState([FOCUS_INTERACTIONS], {
      [FOCUS_INTERACTIONS]: {
        isUserInteractWithComp: true,
      },
    });
  };

  // function to handle the casing for guest display value
  const getLocalizedGuestsDisplayValue = (guestText: string) => {
    if (LOCALES_FOR_ROOMS_AND_GUEST_DISPLAY_VALUE_CHANGES.includes(currentLocale?.toLowerCase() as string)) {
      return guestText;
    } else {
      return capitalize(guestText);
    }
  };

  // function to handle the casing for room display value
  const getLocalizedRoomsDisplayValue = (roomsText: string) => {
    if (LOCALES_FOR_ROOMS_AND_GUEST_DISPLAY_VALUE_CHANGES.includes(currentLocale?.toLowerCase() as string)) {
      return roomsText.toLowerCase();
    } else {
      return roomsText;
    }
  };

  /**
   * returns the string format of rooms and guest count for display
   * @param roomsCount
   * @param adultsCount
   * @param childrenCount
   */
  const getRoomsAndGuestCount = (roomsCount: string, adultsCount: number, childrenCount: number): string => {
    const guestCount = adultsCount + childrenCount;
    const guestText = guestCount > 1 ? guests : guest;
    const guestsDisplayValue = guestText?.includes('{x}')
      ? guestText.replace('{x}', guestCount.toString())
      : `${guestCount} ${getLocalizedGuestsDisplayValue(guestText)}`;
    const roomsCountStr = roomsCount?.toString() || '1';
    const roomsText = roomsCountStr !== '1' ? rooms : room;

    // CNWEB-2606 `unitOfRoom` is only applicable for certain locales like CN
    // For CN pages where it's not applicable, it returns the constant `UNIT_OF_ROOM`
    const unit = unitOfRoom !== UNIT_OF_ROOM ? unitOfRoom : '';
    const roomsDisplayValue = roomsText?.includes('{x}')
      ? roomsText.replace('{x}', roomsCountStr)
      : `${roomsCountStr} ${unit}${getLocalizedRoomsDisplayValue(roomsText)}`;

    if (checkHideRows()) {
      return `${roomsDisplayValue}, ${guestsDisplayValue}`;
    } else {
      return `${roomsDisplayValue}`;
    }
  };

  // To check total guests count (adults + children)
  const totalGuestsCheck = (): boolean => {
    return parseInt(adultsCount['adultsQuantity'], 10) + parseInt(childrenCount['childrenQuantity'], 10) >= totalGuests;
  };
  // To hide reset handler when initial values are set
  const resetHideCheck = (): boolean => {
    return (
      roomsCount['roomQuantity']?.toString() !== roomsMinValue.roomQuantity ||
      adultsCount['adultsQuantity']?.toString() !== adultsMinValue['adultsQuantity'] ||
      childrenCount['childrenQuantity']?.toString() !== childrenMinValue['childrenQuantity']
    );
  };

  const _applyBtnHandler = (): void => {
    // Set flag to prevent onCancel from being called after Apply
    applyClickedRef.current = true;
    setSearchFormState([ROOMS_AND_GUESTS], {
      [ROOMS_AND_GUESTS]: {
        numRooms: roomsCount['roomQuantity'],
        numAdultsPerRoom: parseInt(adultsCount['adultsQuantity'], 10),
        numChildrenPerRoom: parseInt(childrenCount['childrenQuantity'], 10),
        childrenAges: childrenAgeCollection?.map(obj => obj?.value),
      },
    });
    // Note: changeMobileState is called from dropdownModalOnCLoseFunc after the
    // DropDownModal finishes its close sequence, to avoid race conditions.
  };

  const formatChildAge = (childAgeReq: string, index: number) => {
    //TODO:
    return childAgeReq.replace('{x}', (index + 1)?.toString()).replace('(Required)', '');
  };
  const renderChildAges = () => {
    return (
      <div className="child-collection py-2">
        {childrenAgeCollection?.map((element: ChildrenAgeCollectionType, index: number) => (
          <StepperTextBlock
            stepperValue={element.value}
            handleDecrement={(): void => childAgeChangeHandler(index, 'dec')}
            handleIncrement={(): void => childAgeChangeHandler(index, 'inc')}
            copyText={formatChildAge(childAgeRequired, index)}
            elementType={Types.tags.div}
            headingFontSize={Types.size.small}
            disableIncrement={childrenAgeMaxValue['val'] === element.value}
            disableDecrement={!element.isValueSet || childrenAgeMinValue['val'] === element.value}
            increamentLabel={element.isValueSet ? `${add} ${element.value}` : `${add} ${ageLabel}`}
            decreamentLabel={element.isValueSet ? `${remove} ${element.value}` : `${remove} ${ageLabel}`}
            clickTrackloc={CHILD_AGE_CLICK_TRACK}
            variation={'bordered'}
          />
        ))}
      </div>
    );
  };

  const renderStepperOptions = () => {
    const handleResetKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        e.stopPropagation();
        resetHandler();
        setPopupOpenState(false);
      }
    };
    const shouldDisableInc = () => {
      const roomCount = Number(roomsCount['roomQuantity']?.toString()?.split(/[-~+]/)[0]);
      if (
        (isAvailabilityScenario && roomCount >= 3) ||
        roomsMaxValue.roomQuantity === roomsCount['roomQuantity']?.toString()
      )
        return true;
      return false;
    };
    return (
      <div className={isDesktopView ? `px-3 pb-1` : ''}>
        <div className="stepper-text-contianer">
          <StepperTextBlock
            stepperValue={roomsCount['roomQuantity']}
            handleDecrement={(): void => roomsChangeHandler('dec')}
            handleIncrement={(): void => roomsChangeHandler('inc')}
            copyText={(rooms ?? '').includes('{x}') ? (rooms ?? '').split('{x}')[0].trim() : rooms ?? ''}
            elementType={Types.tags.div}
            headingFontSize={Types.size.small}
            disableIncrement={shouldDisableInc()}
            disableDecrement={roomsMinValue.roomQuantity === roomsCount['roomQuantity']?.toString()}
            increamentLabel={`${add} ${rooms}`}
            decreamentLabel={`${remove} ${rooms}`}
            clickTrackloc={ROOMS_CLICK_TRACK}
            variation={'bordered'}
          />
        </div>
        {checkHideRows() && (
          <>
            <div className="stepper-text-contianer">
              <StepperTextBlock
                stepperValue={adultsCount['adultsQuantity']}
                handleDecrement={(): void => adultsChangeHandler('dec')}
                handleIncrement={(): void => adultsChangeHandler('inc')}
                copyText={adults}
                elementType={Types.tags.div}
                subHeadingType={tags.div}
                subHeadingText={roomsAndGuestAdultHelperText?.replace('{x}', maxGuestPerRoom)}
                subHeadingFontSize={size.extraSmall}
                headingFontSize={Types.size.small}
                disableIncrement={totalGuestsCheck()}
                disableDecrement={adultsMinValue['adultsQuantity'] === adultsCount['adultsQuantity']?.toString()}
                increamentLabel={`${add} ${adults}`}
                decreamentLabel={`${remove} ${adults}`}
                clickTrackloc={ADULTS_CLICK_TRACK}
                variation={'bordered'}
              />
            </div>
            <div className="stepper-text-contianer">
              <StepperTextBlock
                stepperValue={childrenCount['childrenQuantity']}
                handleDecrement={(): void => childrenChangeHandler('dec')}
                handleIncrement={(): void => childrenChangeHandler('inc')}
                copyText={children}
                elementType={Types.tags.div}
                subHeadingType={tags.div}
                subHeadingText={roomsAndGuestChildHelperText?.replace('{x}', maxGuestPerRoom)}
                helperText={''}
                subHeadingFontSize={size.extraSmall}
                headingFontSize={Types.size.small}
                disableIncrement={totalGuestsCheck()}
                disableDecrement={
                  childrenMinValue['childrenQuantity'] === childrenCount['childrenQuantity']?.toString()
                }
                increamentLabel={`${add} ${children}`}
                decreamentLabel={`${remove} ${children}`}
                clickTrackloc={CHILD_CLICK_TRACK}
                variation={'bordered'}
              />
              {childrenCount['childrenQuantity'] && childrenAgeCollection && childrenAgeCollection.length > 0
                ? renderChildAges()
                : ''}
            </div>
          </>
        )}
        {isDesktopView && !showDesktopFooter && resetHideCheck() && (
          <div className="pt-3">
            <a
              href="#"
              className="m-link-action custom_click_track"
              onClick={resetHandler}
              title={roomsAndGuestsResetCta}
              data-testid={roomsAndGuestsResetCta}
              onKeyDown={handleResetKeyDown}
              custom_click_track_value={ROOMS_AND_GUESTS_CLICK_TRACK}
            >
              {roomsAndGuestsResetCta}
            </a>
          </div>
        )}
      </div>
    );
  };
  let blurTimeout: string | number | NodeJS.Timeout | undefined; // a timeout function to handle blur after specific amount of time
  const handleIconPopUpClick = (event?: KeyboardEvent<Element> | React.MouseEvent): void => {
    event?.preventDefault(); // preventing default action so that on focus event does not interfer with on click event
    clearTimeout(blurTimeout); // clearing timeout
    onInteraction?.();
    // ADF mobile compact view: open search form modal instead of rooms dropdown
    if (onMobileCompactViewClick && isAvailabilityScenario && !isDesktopView && !isInModal) {
      onMobileCompactViewClick();
    } else {
      const newState = !popupOpenState;
      setPopupOpenState(newState);
      if (newState) {
        setActivePopup('roomsAndGuests');
      } else if (activePopup === 'roomsAndGuests') {
        setActivePopup(null);
      }
    }
    //capture user interactions
    setSearchFormState([FOCUS_INTERACTIONS], { [FOCUS_INTERACTIONS]: { isUserInteractWithComp: true } });
  };
  const inputRef = useRef<HTMLInputElement>(null); // ref to point parent div of input element

  // Function to close dropdown when focus leaves both input and dropdown
  const handleBlur = (event: any) => {
    const relatedTarget = event.relatedTarget as Element;
    // Don't close if focus moved to an external modal (privacy popup, Adobe experiments, etc.)
    if (shouldIgnoreFocusOutside(relatedTarget)) {
      return;
    }
    if (relatedTarget && !inputRef?.current?.contains(relatedTarget)) {
      setPopupOpenState(false);
      if (activePopup === 'roomsAndGuests') {
        setActivePopup(null);
      }
    }
  };
  const getRoomsAndGuestsInput = (isDesktop: boolean, isNextGenHp: boolean | undefined) => {
    if (isNextGenHp && isDesktop) {
      return (
        <div ref={customSelectRef} data-mini-trigger="rooms">
          <CustomSelectBlock
            customClickTrackValue={`${searchFormClickTrackingLoc} | Rooms and Guests |internal`}
            selectTitle={roomsAndGuestEyebrowText}
            selectDescription={getRoomsAndGuestCount(
              roomsCount['roomQuantity'],
              +adultsCount['adultsQuantity'],
              +childrenCount['childrenQuantity']
            )}
            handleClick={showPopup}
            name={roomsAndGuestEybrowText}
            id={roomsAndGuestEybrowText}
            isExpanded={popupOpenState}
            changeArrowOnOpen={true}
            customButtonClass="t-font-weight-m t-font-xs"
            additionalPaddingClass={isNextGenHp && 'pr-0'}
            bothLabelArrowClick={true}
          />
        </div>
      );
    } else {
      // Mobile view for both NextGenHP and NextGen standard, and desktop NextGen standard
      const roomsGuestsDisplayValue = getRoomsAndGuestCount(
        roomsCount['roomQuantity'] || '1',
        +adultsCount['adultsQuantity'],
        +childrenCount['childrenQuantity']
      );

      const inputField = (
        <InputTextField
          custom_click_track_value={`${searchFormClickTrackingLoc} | Rooms and Guests |internal`}
          handleKeyDown={() => {
            onInteraction?.();
            // ADF mobile compact view: open search form modal instead of rooms dropdown (same as dates)
            if (onMobileCompactViewClick && isAvailabilityScenario && !isDesktopView && !isInModal) {
              onMobileCompactViewClick();
            } else {
              const newState = !popupOpenState;
              setPopupOpenState(newState);
              if (newState) {
                setActivePopup('roomsAndGuests');
              } else if (activePopup === 'roomsAndGuests') {
                setActivePopup(null);
              }
            }
          }}
          onBlur={handleBlur}
          className={'m-input-text-field party-mix-input-container'} // these 2 class names are mandatory , please do not remove
          label={roomsAndGuestEyebrowText}
          inputValue={roomsGuestsDisplayValue}
          inputTextFieldClassName={clsx(
            !isNextGenHp ? 'rooms-guests-m-font' : '',
            // Add rooms-guests-input-field class for NextGen standard (both desktop and mobile)
            // This enables the is-active/is-inactive styling
            !isNextGenHp ? 'rooms-guests-input-field' : ''
          )}
          showIcon={true}
          isClickEnabled={!isNextGenHp && isDesktop}
          iconClass={isDesktopView ? (popupOpenState ? 'icon-arrow-up' : 'icon-arrow-down') : 'icon-arrow-down'}
          iconOnClick={handleIconPopUpClick}
          trailingIconProps={{
            className: clsx('trailing-element', 'party-mix-arrow-icon'), // these 2 class names are mandatory , please do not remove
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.code === '13') {
                onInteraction?.();
                handleIconPopUpClick(e);
              }
            },
          }}
          getInputProps={() => ({
            readOnly: true,
            onKeyDown: (ev: React.KeyboardEvent<HTMLInputElement>) => {
              if (ev.key === 'Enter') {
                // Handle inactive state: First Enter should activate the form
                if (!isSearchFormActiveState && !isNextGenHp && isDesktopView) {
                  ev.preventDefault();
                  setIsSearchFormActiveState(true);
                  // Focus will remain on the same input after state change
                  setTimeout(() => {
                    // Use the event target instead of querySelector
                    const roomsGuestsInput = ev.target as HTMLInputElement;
                    if (roomsGuestsInput) {
                      roomsGuestsInput.focus();
                    }
                  }, 100);
                  return;
                }

                onInteraction?.();
                const newState = !popupOpenState;
                setPopupOpenState(newState);
                if (newState) {
                  setActivePopup('roomsAndGuests');
                } else if (activePopup === 'roomsAndGuests') {
                  setActivePopup(null);
                }
              }
            },
          })}
          readOnly={true}
          variation={'default'}
          withinModal={!isDesktopView}
          showUnderline={!isDesktopView}
          iconAriaLabel={popupOpenState ? 'close' : 'open'}
        ></InputTextField>
      );

      // Wrap with div for NextGen standard (is-active/is-inactive styling) - both desktop and mobile
      if (!isNextGenHp) {
        // For NextGen standard:
        // - Desktop: use isSearchFormActiveState from store
        // - Mobile inside modal: always active (modal = editing mode)
        // - Mobile outside modal: always inactive
        const shouldShowActiveState = isDesktopView
          ? isSearchFormActiveState
          : isInModal
          ? true // Mobile inside modal = always active
          : false; // Mobile outside modal = always inactive

        // Calculate width - only measure on client after mount to avoid SSR hydration mismatch
        // SSR returns fallback (text.length * 10), client measures actual font width
        const inputWidth = isClientMounted ? measureTextWidth(roomsGuestsDisplayValue) + 16 : undefined; // Let CSS handle width during SSR/initial render

        return (
          <div
            className={clsx('rooms-guests-input-wrapper', shouldShowActiveState ? 'is-active' : 'is-inactive')}
            style={
              !shouldShowActiveState && inputWidth
                ? ({ '--input-ch-width': `${inputWidth}px` } as React.CSSProperties)
                : undefined
            }
          >
            {!shouldShowActiveState && (
              <span className="rooms-guests-icon-wrapper">
                <span className="icon-room-ready" />
              </span>
            )}
            {inputField}
          </div>
        );
      }

      return inputField;
    }
  };
  return (
    <StyledRoomsAndGuests
      data-component-name="m-shop-RoomsAndGuests"
      data-testid="shop-RoomsAndGuests"
      $isNextGenHP={isNextGenHp}
      $buttonOffset={buttonOffset}
      className={clsx(
        !isNextGenHp && isDesktopView && isSearchFormActiveState ? 'is-active' : '',
        !isNextGenHp && popupOpenState ? 'dropdown-open' : 'rooms-and-guests'
      )}
      // data-testid="rooms-and-guests"
      onClick={() => {
        onInteraction?.();
        if (!isDesktopView && !popupOpenState) {
          // ADF mobile compact view: open search form modal first (same as dates)
          if (onMobileCompactViewClick && isAvailabilityScenario && !isInModal) {
            onMobileCompactViewClick();
          } else {
            showPopup();
          }
        }
      }}
      ref={inputRef}
    >
      {getRoomsAndGuestsInput(!!isDesktopView, isNextGenHp)}
      {popupOpenState && (
        <DropDownModal
          id={modalId}
          role="dialog"
          shouldAutoFocusOnPopup={true}
          isAutoScrollEnabled={isNextGenHp ? true : false}
          parentElementTopValue={searchFormTopPosition?.value}
          applyDefaultHeight={true}
          children={renderStepperOptions()}
          className={clsx(
            'dropdown-container',
            !isNextGenHp && 'rooms-guests-dropdown-container',
            isDesktopView && !isNextGenHp ? 'tabbed-form-dropdown-container' : '',
            'party-mix-dropdown-container' // this class name is mandatory , please do not remove
          )}
          childrenClassName={clsx('room-guest-dropdown')}
          show={popupOpenState}
          clearButtonLabel={resetHideCheck() ? roomsAndGuestsResetCta : ''}
          applyLabel={roomsAndGuestsDoneCta}
          applyTrackLabel={applyTrackLabel}
          mobileModalHeading={roomsAndGuestEyebrowText}
          dropdownModalCloseClassName={'rooms-guests-close'}
          clickTrackingLoc={'Rooms & Guests Dropdown'}
          labelledBy={'dropdown-modal-container'}
          mobileTakeOver={!isDesktopView ? true : false}
          desktopDropdown={showDesktopFooter}
          handleBlur={true}
          dropDownSpecificFlag={true}
          mobileHeaderEnabled={true}
          mobileFooterEnabled={true}
          scrollDownModalCloseState={true}
          setDropdownModalOpenState={setPopupOpenState}
          dropdownModalOpenState={popupOpenState}
          callhandleBlur={handleBlur}
          applyBtnClickHandler={() => {
            // Set flag and immediately call changeMobileState / onCancel as appropriate.
            // We bypass dropdownModalOnCLoseFunc entirely because DropDownModal calls it
            // BEFORE applyBtnClickHandler, so the flag would always read false there.
            applyClickedRef.current = true;
            setSearchFormState([ROOMS_AND_GUESTS], {
              [ROOMS_AND_GUESTS]: {
                numRooms: roomsCount['roomQuantity'],
                numAdultsPerRoom: parseInt(adultsCount['adultsQuantity'], 10),
                numChildrenPerRoom: parseInt(childrenCount['childrenQuantity'], 10),
                childrenAges: childrenAgeCollection?.map(obj => obj?.value),
              },
            });
            changeMobileState?.();
          }}
          dropdownModalOnCLoseFunc={() => {
            setTimeout(() => {
              // Only call onCancel if Apply was NOT clicked (user clicked X to close)
              if (!applyClickedRef.current) {
                onCancel?.();
              }
              // Do NOT reset applyClickedRef here — DropDownModal calls this function
              // twice for Apply clicks, causing a race. It resets on next forceOpen instead.
              setPopupOpenState(false);
              if (activePopup === 'roomsAndGuests') {
                setActivePopup(null);
              }
            }, 100);
          }}
          clearClickHandler={
            resetHideCheck()
              ? resetHandler
              : (): void => {
                  return;
                }
          }
        />
      )}
    </StyledRoomsAndGuests>
  );
};
